import { zodResolver } from "@hookform/resolvers/zod";
import { staticPath } from "mypage/src/lib/$path";
import useTranslation from "next-translate/useTranslation";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import styled, { css } from "styled-components";
import { CircularProgress } from "~/components/atoms/CircularProgress";
import Image from "~/components/atoms/Image";
import Radio from "~/components/atoms/Radio";
import TextAreaField from "~/components/atoms/form/TextAreaField";
import useTalkRoomCommentForm from "~/hooks/useTalkRoomCommentForm";
import { breakPoint, colorBlack, colorPrimary } from "~/lib/constants";
import { errorWithoutThrowInProduction } from "~/lib/logging/logHelper";
import { showToast } from "~/lib/toast/showToast";
import {
  getAvailableQaPublishControl,
  QuestionAnswer,
} from "~/models/questionAnswer";
import {
  getArticleEvaluationDisplayName,
  QaArticleEvaluation,
  QA_ARTICLE_EVALUATION_TYPES,
} from "~/models/questionAnswer/articleEvaluation";
import { QuestionAnswerContentRevision } from "~/models/questionAnswer/contentRevision";
import {
  QaReviewComment,
  QaReviewCommentMode,
  QA_REVIEW_COMMENT_SCHEMA,
} from "~/models/questionAnswer/reviewComment";
import { unstyledButton } from "~/styles/CommonStyles";

export type QaCommentCallbackData = {
  content: QaReviewComment["content"];
  line: QaReviewComment["line"];
  targetQaField: QaReviewComment["targetQaField"];
  targetText: QaReviewComment["targetText"];
};

export type TalkRoomCommentCallbackData = {
  message: string;
  mode: "reviewComplete" | "publish";
};

export type QaArticleEvaluationCallbackData = {
  comment: string;
  evaluationType: QaArticleEvaluation["type"];
  line: QaArticleEvaluation["line"];
  targetQaField: QaArticleEvaluation["targetQaField"];
  targetText: QaArticleEvaluation["targetText"];
};

export type QaResizeImageCallbackData = {
  imageSrc: string;
  size?: number;
  imageAlt: string;
};

export type CommentCallbackResponse = { error?: Error; isSuccess: boolean };

interface Props {
  evaluationType?: QaArticleEvaluation["type"] | null;
  line?: QaReviewComment["line"];
  onClickEvaluationType?: ({
    evaluationType,
  }: {
    evaluationType: QaArticleEvaluation["type"] | null;
  }) => void;
  onSuccess?: () => void;
  onSuccessQaReviewComment?: () => void;
  onSuccessTalkRoomComment?: () => void;
  onSubmitQaArticleEvaluationCallback?: (
    data: QaArticleEvaluationCallbackData
  ) => Promise<CommentCallbackResponse>;
  onSubmitQaReviewCommentCallback?: (
    data: QaCommentCallbackData
  ) => Promise<CommentCallbackResponse>;
  onSubmitTalkRoomCommentCallback?: (
    data: TalkRoomCommentCallbackData
  ) => Promise<CommentCallbackResponse>;
  questionAnswer: QuestionAnswer;
  reviewMode?: QaReviewCommentMode;
  revisionStatus?: QuestionAnswerContentRevision["status"];
  targetImgSrc?: string;
  targetQaField: QaReviewComment["targetQaField"];
  targetText?: string;
  taskId?: string;
  withTalkRoomForm: boolean;
}

interface IFormInputs {
  content: string;
  targetText: string;
}

type RadioId = "overview" | "reviewComplete" | "publish";

const isValidReviewOption = (optionId: string): optionId is RadioId => {
  return ["overview", "reviewComplete", "publish"].includes(optionId);
};

const QaReviewCommentForm: React.VFC<Props> = ({
  evaluationType,
  line = 0,
  onClickEvaluationType,
  onSuccess,
  onSuccessQaReviewComment,
  onSuccessTalkRoomComment,
  onSubmitQaArticleEvaluationCallback,
  onSubmitQaReviewCommentCallback,
  onSubmitTalkRoomCommentCallback,
  questionAnswer,
  reviewMode = "forAdminUser",
  revisionStatus,
  targetImgSrc,
  targetQaField,
  targetText,
  taskId,
  withTalkRoomForm,
}) => {
  const { t } = useTranslation();
  const [checkedRadio, setCheckedRadio] = useState<RadioId | null>(
    withTalkRoomForm ? "overview" : null
  );

  const {
    handleSubmit: handleSubmitQaReviewComment,
    formState: {
      isValid: isQaReviewCommentValid,
      isSubmitting: isQaReviewCommentSubmitting,
    },
    register: registerQaReviewComment,
    setValue: setQaReviewComment,
    trigger: triggerQaReviewComment,
    watch: watchQaReviewComment,
  } = useForm<IFormInputs>({
    defaultValues: {
      content: evaluationType
        ? `[${getArticleEvaluationDisplayName(evaluationType)}]`
        : "",
      // Text has a higher priority than images.
      // (Images are ignored if text is selected)
      targetText: targetText?.replace(/\s/g, "") || targetImgSrc || "",
    },
    mode: "onChange",
    resolver: zodResolver(QA_REVIEW_COMMENT_SCHEMA),
    shouldUnregister: false,
  });

  useEffect(() => {
    setQaReviewComment(
      "content",
      evaluationType
        ? `[${getArticleEvaluationDisplayName(evaluationType)}]`
        : ""
    );
    // revalidate after set value
    triggerQaReviewComment();
  }, [evaluationType, setQaReviewComment, triggerQaReviewComment]);

  const {
    formState: {
      isValid: isTalkRoomCommentValid,
      isSubmitting: isTalkRoomCommentSubmitting,
    },
    handleSubmit: handleSubmitTalkRoomComment,
    register: registerTalkRoomComment,
    setValue: setTalkRoomComment,
    trigger: triggerTalkRoomComment,
    watch: watchTalkRoomComment,
  } = useTalkRoomCommentForm();

  const availableQaPublishControl = getAvailableQaPublishControl(
    questionAnswer.displayStatus,
    revisionStatus
  );
  const canPublish =
    availableQaPublishControl === "publishArticle" ||
    availableQaPublishControl === "publishChanges";

  const isQaReviewCommentForm =
    !withTalkRoomForm || checkedRadio === "overview";

  const fieldName = isQaReviewCommentForm ? "content" : "message";

  const isValid = isQaReviewCommentForm
    ? isQaReviewCommentValid
    : isTalkRoomCommentValid;

  const isSubmitting = isQaReviewCommentForm
    ? isQaReviewCommentSubmitting
    : isTalkRoomCommentSubmitting;

  const onSubmitQaReviewComment = handleSubmitQaReviewComment(async (data) => {
    if (checkedRadio === "publish" || checkedRadio === "reviewComplete") {
      return;
    }
    try {
      if (onSubmitQaArticleEvaluationCallback && evaluationType) {
        const { error, isSuccess } = await onSubmitQaArticleEvaluationCallback({
          comment: data.content,
          evaluationType,
          line: line || 0,
          targetQaField,
          targetText: data.targetText,
        });
        if (!isSuccess) {
          throw error ?? Error("Fail to create QaArticleEvaluation");
        }
      } else if (onSubmitQaReviewCommentCallback) {
        const { error, isSuccess } = await onSubmitQaReviewCommentCallback({
          content: data.content,
          // Title and description don't have line number.
          line: targetQaField === "answerContent" ? line : 0,
          targetQaField: withTalkRoomForm ? "overview" : targetQaField,
          targetText: data.targetText,
        });
        if (!isSuccess) {
          throw error ?? Error("Fail to create QaReviewComment");
        }
      }
      onSuccessQaReviewComment?.();
      onSuccess?.();

      showToast(t("question:message.review-sent"));
    } catch (e) {
      showToast("送信に失敗しました");
      errorWithoutThrowInProduction({
        errorInfo: { reviewMode, stack: e?.stack },
        errorMessage: "Failed to post qaReviewComment",
      });
    }
  });

  const onSubmitTalkRoomComment = handleSubmitTalkRoomComment(async (data) => {
    try {
      if (checkedRadio === null || checkedRadio === "overview" || !taskId) {
        throw new Error(
          "Invalid checkedRadio or taskId (shouldn't reach here)"
        );
      }

      switch (checkedRadio) {
        case "publish":
          if (!canPublish) {
            throw new Error(
              `Tried to publish qa even though it can't be. availableQaPublishControl: ${availableQaPublishControl}`
            );
          }
          await onSubmitTalkRoomCommentCallback?.({
            message: data.message,
            mode: "publish",
          });
          break;
        case "reviewComplete":
          await onSubmitTalkRoomCommentCallback?.({
            message: data.message,
            mode: "reviewComplete",
          });
          break;
        default:
          throw new Error("Invalid checkedRadio (shouldn't reach here)");
      }
      onSuccessTalkRoomComment?.();
      onSuccess?.();
    } catch (e) {
      showToast("送信に失敗しました");
      errorWithoutThrowInProduction({
        errorInfo: { error: e?.message, questionAnswer },
        errorMessage: "Failed to post qaReviewComment",
      });
    }
  });

  const qaReviewCommentContent = watchQaReviewComment("content");
  const talkRoomCommentMessage = watchTalkRoomComment("message");
  const handleRadioChange = (id: string) => {
    if (!isValidReviewOption(id)) {
      return;
    }

    // ensure the right value is set when switching menu
    if (
      checkedRadio === "overview" &&
      ["reviewComplete", "publish"].includes(id)
    ) {
      setTalkRoomComment("message", qaReviewCommentContent);
    } else if (
      id === "overview" &&
      checkedRadio &&
      ["reviewComplete", "publish"].includes(checkedRadio)
    ) {
      setQaReviewComment("content", talkRoomCommentMessage);
    }
    // Ensure validations are triggered.
    triggerQaReviewComment();
    triggerTalkRoomComment();
    setCheckedRadio(id);
  };

  // Synchronize values.
  useEffect(() => {
    if (isQaReviewCommentForm && !qaReviewCommentContent) {
      setQaReviewComment("content", talkRoomCommentMessage);
      // revalidate after set value
      triggerQaReviewComment();
    }
  }, [
    isQaReviewCommentForm,
    setQaReviewComment,
    talkRoomCommentMessage,
    qaReviewCommentContent,
    triggerQaReviewComment,
  ]);

  useEffect(() => {
    if (!isQaReviewCommentForm && !talkRoomCommentMessage) {
      setTalkRoomComment("message", qaReviewCommentContent);
      // revalidate after set value
      triggerTalkRoomComment();
    }
  }, [
    isQaReviewCommentForm,
    qaReviewCommentContent,
    setTalkRoomComment,
    talkRoomCommentMessage,
    triggerTalkRoomComment,
  ]);

  const publishLabel = useMemo(() => {
    switch (availableQaPublishControl) {
      case "publishArticle":
        return "記事公開";
      case "publishChanges":
        return "変更公開";
      default:
        return "";
    }
  }, [availableQaPublishControl]);

  return (
    <Container theme={targetQaField === "overview" ? "light" : undefined}>
      {!withTalkRoomForm && targetText && (
        <SelectedText>
          <SelectedTextContentScroll>
            {/* Ignore image if text is selected */}
            {targetImgSrc && !targetText && (
              <SelectedImgContainer>
                <SelectedImage alt="Selected image" src={targetImgSrc} />
              </SelectedImgContainer>
            )}
            <SelectedTextContent>{targetText}</SelectedTextContent>
          </SelectedTextContentScroll>
        </SelectedText>
      )}
      <FormContainer>
        <Form
          onSubmit={
            isQaReviewCommentForm
              ? onSubmitQaReviewComment
              : onSubmitTalkRoomComment
          }
        >
          <MessageRow>
            <MessageIconWrapper>
              <Image
                src={staticPath.img.icon_comment_svg}
                alt="message"
                height={24}
                width={24}
                disableHover
              />
            </MessageIconWrapper>
            <MessageBox>
              <TextAreaField
                name={fieldName}
                id={fieldName}
                placeholder="どう思いましたか？"
                register={
                  isQaReviewCommentForm
                    ? registerQaReviewComment("content")
                    : registerTalkRoomComment("message")
                }
                rows={1}
              />
              <SendButton type="submit" disabled={!isValid || isSubmitting}>
                {isSubmitting ? (
                  <CircularProgress size={24} margin={0} />
                ) : (
                  <Image
                    src={staticPath.img.icon_paper_airplane_svg}
                    alt="send comment"
                    height={24}
                    width={24}
                    disableHover
                  />
                )}
              </SendButton>
            </MessageBox>
          </MessageRow>
          {withTalkRoomForm && (
            <RadioGroup>
              <Radio
                checked={checkedRadio === "overview"}
                id="overview"
                name="qaReview"
                value="overview"
                onChange={handleRadioChange}
              >
                記事全体
              </Radio>
              {taskId && (
                <>
                  <Radio
                    checked={checkedRadio === "reviewComplete"}
                    id="reviewComplete"
                    name="qaReview"
                    value="reviewComplete"
                    onChange={handleRadioChange}
                  >
                    レビュー完了
                  </Radio>
                  {canPublish && (
                    <Radio
                      checked={checkedRadio === "publish"}
                      id="publish"
                      name="qaReview"
                      value="publish"
                      onChange={handleRadioChange}
                    >
                      {publishLabel}
                    </Radio>
                  )}
                </>
              )}
            </RadioGroup>
          )}
        </Form>
        {onClickEvaluationType && (
          <EvaluationTypeButtonGroup>
            {[null, ...QA_ARTICLE_EVALUATION_TYPES].map((type, i) => {
              return (
                <EvaluationTypeButton
                  key={i}
                  isSelected={type === evaluationType}
                  onClick={() =>
                    onClickEvaluationType?.({ evaluationType: type })
                  }
                >
                  {type
                    ? getArticleEvaluationDisplayName(type)
                    : "通常コメント"}
                </EvaluationTypeButton>
              );
            })}
          </EvaluationTypeButtonGroup>
        )}
      </FormContainer>
    </Container>
  );
};

// Depends on implementation of TextAreaField
const SINGLE_LINE_TEXTAREA_HEIGHT = 56 as const;

const Container = styled.div<{ theme?: "light" | "dark" }>`
  background-color: var(--body-bg-clr);
  display: grid;
  grid-row-gap: 32px;
  height: 100%;

  ${({ theme }) => {
    if (theme === "light") {
      return css`
        @media (prefers-color-scheme: dark) {
          --body-bg-clr: #fff;
          --body-clr: ${colorBlack[600]};
          --pri-clr500: ${colorPrimary[500]};
        }
      `;
    }
  }};
`;

const SelectedText = styled.div``;

const SelectedImgContainer = styled.div`
  margin-bottom: 8px;
  & > *:not(:first-child) {
    margin-left: 8px;
  }
`;

const SelectedTextContentScroll = styled.div`
  max-height: 140px;
  overflow-y: auto;
  padding-right: 8px;
`;

const SelectedImage = styled.img`
  max-width: 100%;
`;

const SelectedTextContent = styled.blockquote`
  color: var(--sub-txt-clr);
  border-left: solid 2px var(--b-clr200);
  padding-left: 16px;
  word-break: break-all;
`;

const FormContainer = styled.div``;

const Form = styled.form`
  display: grid;
  grid-row-gap: 24px;
`;

const MessageRow = styled.div`
  display: flex;
  gap: 16px;
`;

const MessageBox = styled.div`
  display: grid;
  grid-template-columns: 1fr 24px;
  column-gap: 12px;
  align-items: flex-start;
  width: 100%;
`;

const MessageIconWrapper = styled.div`
  display: grid;
  height: ${SINGLE_LINE_TEXTAREA_HEIGHT}px;
  align-items: center;

  @media screen and (max-width: ${breakPoint["sm"]}) {
    display: none;
  }
`;

const SendButton = styled.button`
  ${unstyledButton};

  height: ${SINGLE_LINE_TEXTAREA_HEIGHT}px;

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  column-gap: 12px;
`;

const EvaluationTypeButtonGroup = styled.div`
  margin-top: 20px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  flex-wrap: wrap;
`;

const EvaluationTypeButton = styled.button<{ isSelected: boolean }>`
  ${unstyledButton}

  background-color: ${({ isSelected }) =>
    isSelected ? colorPrimary[600] : "var(--hv-box-bg-clr)"};
  border-radius: 5px;
  color: ${({ isSelected }) => (isSelected ? "#fff" : "var(--pri-clr600)")};
  font-size: 16px;
  font-weight: 700;
  line-height: 30px;
  padding: 2px 12px;
  text-align: center;

  &:hover {
    text-decoration: underline;
  }
`;

export default QaReviewCommentForm;
